import { noop } from 'lodash';
import { useRef } from 'react';
import intl from '../../locale';
import { usePollStatePredicate, usePollTransitionAbilityPredicate } from '../../reducer/machine/utils';

export const useControlsProps = ({
  isLoading,
  hasTextField,
  hasMultipleAnswerQuestion,
  isAllQuestionsOptional,
  isAllRequiredQuestionsAnswered,
  isAllQuestionsAnswered,
  isSomeQuestionsAnswered,
  isSequentialDisplayGroup,
  textFieldDisclaimer,
  onAnswer,
  onTryAnswer,
  onNext,
  onSkip,
  autoCommit,
  systemButtonText = {},
}: {
  hasTextField: boolean;
  hasMultipleAnswerQuestion: boolean;
  isAllQuestionsOptional: boolean;
  isAllRequiredQuestionsAnswered: boolean;
  isAllQuestionsAnswered: boolean;
  isSomeQuestionsAnswered: boolean;
  isSequentialDisplayGroup: boolean | undefined;
  textFieldDisclaimer?: string;
  isLoading: boolean;
  autoCommit: boolean;
  onTryAnswer: VoidFunction;
  onAnswer: VoidFunction;
  onNext: VoidFunction;
  onSkip: VoidFunction;
  systemButtonText:
    | {
        next?: string;
        answer?: string;
        skip?: string;
      }
    | undefined;
}) => {
  const canSkipQuestion = usePollTransitionAbilityPredicate({ type: 'skip' });
  const canGoNext = usePollTransitionAbilityPredicate({ type: 'next' });
  const canAnswer = usePollTransitionAbilityPredicate({ type: 'answer' });
  const canTryAnswer = usePollTransitionAbilityPredicate({ type: 'tryAnswer' });
  const onFeedbackScreen = usePollStatePredicate('questions.screen.answered.feedback');

  const prevCanSkip = useRef(canSkipQuestion);
  const canSkip = isLoading ? prevCanSkip.current : canSkipQuestion;
  prevCanSkip.current = canSkip;

  const hint = hasTextField ? textFieldDisclaimer : undefined;

  const skipButtonText = systemButtonText.skip?.trim() || intl.formatMessage({ id: 'System.Dismiss' });
  const answerButtonText = canGoNext
    ? systemButtonText.next?.trim() || intl.formatMessage({ id: 'System.Next' })
    : systemButtonText.answer?.trim() || intl.formatMessage({ id: 'System.Answer' });
  const hasUnansweredOptionalQuestions = isAllRequiredQuestionsAnswered && !isAllQuestionsAnswered;
  const isNonSequentialGroupWithUnansweredOptionalQuestions =
    isSequentialDisplayGroup === false &&
    hasUnansweredOptionalQuestions &&
    (!isAllQuestionsOptional || isSomeQuestionsAnswered);

  const showAnswerButton =
    !autoCommit ||
    canGoNext ||
    onFeedbackScreen ||
    hasTextField ||
    hasMultipleAnswerQuestion ||
    isNonSequentialGroupWithUnansweredOptionalQuestions;

  return {
    hint,
    isLoading,
    skipButtonText,
    answerButtonText,
    onAnswer: canGoNext ? onNext : canTryAnswer ? onTryAnswer : canAnswer ? onAnswer : noop,
    showFooter: showAnswerButton || canSkip,
    showAnswerButton,
    ...(canSkip && { canSkip, onSkip }),
  };
};
